import { useLabelsByPartTextQuery } from "../../../../core/api/base/other";
import {
  useVacationAutocompleteRecruiterQuery,
  useVacationAutocompleteTitleQuery,
} from "../../../../core/api/vacancy/vacancy";
import { useRightSidebarState } from "../../../../hooks/useRightSidebarState";
import { checkEmail, checkPhone } from "../../../../services/check.service";
import { formatISODate } from "../../../../services/date.service";
import RightSidebar from "../../../BaseComponents/RightSidebar/RightSidebar";
import { IAuthError } from "../../../NotAuthComponents/Auth/types";
import Card from "../Card/Card";
import { IRequestSearchByFilter, LabelSearchModel } from "../types";
import style from "./FilterSideBar.module.scss";
import {
  Button,
  Checkbox,
  Input,
  InputDatepicker,
  Multiselect,
  MultiselectData,
  Radio,
  RadioFormGroup,
} from "@aurora/components";
import { GlyphSearch } from "@aurora/icons";
import cx from "classnames";
import {
  useCandidateAutocompleteQuery,
  useFindByEmailMutation,
  useFindByPhoneMutation,
} from "core/api/candidate/candidate";
import React, { FC, useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useSearchParams } from "react-router-dom";

interface ICandidatesProps {
  setQueryParams: (params: any) => void;
}

interface State {
  CandidateField: string;
  datepickerValue: (Date | null)[];
  has_new_message: boolean;
}

function initialState(): State {
  return {
    CandidateField: "",
    datepickerValue: [null, null],
    has_new_message: false,
  };
}

const FilterSideBar: FC<ICandidatesProps> = ({ setQueryParams }) => {
  const [state, setState] = useState(initialState);
  const [needClear, setNeedClear] = useState(false);
  const [isFirstLoad, changeFirstLoad] = useState(true);

  const [autocompleteCandidate, changeAutocompleteCandidate] = useState("");
  const [autocompleteVacancy, setAutocompleteVacancy] = useState("");
  const [autocompleteRecruiter, setAutocompleteRecruiter] = useState("");
  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [candidateData, setCandidateData] = useState<MultiselectData[]>([]);
  const [vacancyData, setVacancyData] = useState<MultiselectData[]>([]);
  const [recruiterData, setRecruiterData] = useState<MultiselectData[]>([]);
  const [labelAutocomplete, setLabelAutocomplete] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [labelData, setLabelData] = useState<MultiselectData[]>([]);
  const [candidateSelected, setCandidateSelected] = useState<any>({});
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useRightSidebarState();
  const [labelSearchLogic, setLabelSearchLogic] =
    useState<LabelSearchModel>("OR");
  const {
    data: candidateAutocompleteData,
    error: candidateAutocompleteError,
    isLoading: candidateAutocompleteLoading,
  } = useCandidateAutocompleteQuery(
    { query: autocompleteCandidate },
    {
      skip: autocompleteCandidate.length < 3,
    },
  );
  const {
    data: vacancyAutocompleteData,
    error: vacancyAutocompleteError,
    isLoading: vacancyAutocompleteLoading,
  } = useVacationAutocompleteTitleQuery(autocompleteVacancy, {
    skip: autocompleteVacancy.length < 3,
  });
  const {
    data: recruiterAutocompleteData,
    error: recruiterAutocompleteError,
    isLoading: recruiterAutocompleteLoading,
  } = useVacationAutocompleteRecruiterQuery(autocompleteRecruiter, {
    skip: autocompleteRecruiter.length < 3,
  });
  const {
    data: labelAutocompleteData,
    error: labelAutocompleteError,
    isLoading: labelAutocompleteLoading,
  } = useLabelsByPartTextQuery(labelAutocomplete, {
    skip: labelAutocomplete.length < 3,
  });

  const [notFoundError, setNotFoundError] = useState<IAuthError>({
    description: "",
    status: "success",
  });

  const [checkPersonByEmail] = useFindByEmailMutation();
  const [checkPersonByPhone] = useFindByPhoneMutation();

  useEffect(() => {
    if (candidateAutocompleteError)
      console.error("candidateAutocompleteError", candidateAutocompleteError);
    if (vacancyAutocompleteError)
      console.error("vacancyAutocompleteError", vacancyAutocompleteError);
    if (recruiterAutocompleteError)
      console.error("recruiterAutocompleteError", recruiterAutocompleteError);
    if (labelAutocompleteError)
      console.error("labelAutocompleteError", labelAutocompleteError);
  }, [
    candidateAutocompleteError,
    vacancyAutocompleteError,
    recruiterAutocompleteError,
    labelAutocompleteError,
  ]);

  const handleChange = (field: keyof State, value: any) => {
    setState(prevState => ({ ...prevState, [field]: value }));
  };

  const handleSubmit = () => {
    if (isFirstLoad) {
      changeFirstLoad(false);
      return;
    }
    setQueryParams((oldParams: IRequestSearchByFilter) => {
      return {
        ...oldParams,
        has_new_message: state.has_new_message,
        labelIds: labelData.map(label => label.id),
        labelSearchMode: labelSearchLogic,
        pageNum: 1,
        personNames: candidateData.map(candidate => candidate.value),
        recruiterIds: recruiterData.map(recruiter => recruiter.id),
        resumeUploadPeriodEnd: formatISODate(state.datepickerValue[1]),
        resumeUploadPeriodStart: formatISODate(state.datepickerValue[0]),
        vacancyIds: vacancyData.map(vacancy => vacancy.id),
      } as IRequestSearchByFilter;
    });
  };

  const handleEmailOrPhoneChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setEmailOrPhone(value);

      if (!value || (!checkEmail(value) && !checkPhone(value))) {
        setNotFoundError({ description: "", status: "success" });
        setIsRightSidebarOpen(false);
        setCandidateSelected({});
        setSearchParams({});
        return null;
      }

      let res: any;
      if (checkEmail(value)) {
        res = await checkPersonByEmail({ email: value });
      }

      if (checkPhone(value)) {
        res = await checkPersonByPhone({ phone: value });
      }

      if (res && "error" in res && res.error.status === 404) {
        setNotFoundError({ description: "Ничего не найдено", status: "error" });
        return;
      }

      if (res && "data" in res) {
        setNotFoundError({ description: "", status: "success" });
        setIsRightSidebarOpen(true);
        setCandidateSelected(res?.data);
      }
    },
    [checkPersonByEmail, checkPersonByPhone],
  );

  const handleReset = () => {
    setCandidateData([]);
    setVacancyData([]);
    setRecruiterData([]);
    setState(initialState());
    setLabelSearchLogic("OR");
    setLabelData([]);
    setNeedClear(true);
    setEmailOrPhone("");
    setCandidateSelected({});
  };

  const handleCloseSidebar = () => {
    setCandidateSelected({});
    setEmailOrPhone(""); // Очищаем поле ввода
    setIsRightSidebarOpen(false);
    setSearchParams({});
  };

  useEffect(() => {
    if (!needClear) return;
    // handleSubmit();
    setNeedClear(false);
  }, [needClear]);

  useEffect(() => {
    handleSubmit();
  }, [
    recruiterData,
    vacancyData,
    candidateData,
    state,
    labelData,
    labelSearchLogic,
  ]);

  useEffect(() => {
    setIsRightSidebarOpen(false); // Resets on component mount
  }, []);

  return (
    <>
      <div className="mb-8">
        <Multiselect
          dataFromApi
          addonBefore={<GlyphSearch />}
          className="mb-0"
          data={candidateAutocompleteData || []}
          indent={false}
          loading={candidateAutocompleteLoading}
          onChange={setCandidateData}
          onInputChange={changeAutocompleteCandidate}
          placeholder="Фамилия кандидата"
          shape="select"
          showArrow={false}
          value={candidateData}
        />
      </div>
      <div className="mb-8">
        <Input
          addonBefore={<GlyphSearch />}
          className="mb-0"
          description={notFoundError.description}
          onChange={handleEmailOrPhoneChange}
          placeholder="E-mail или телефон кандидата"
          status={notFoundError.status}
          value={emailOrPhone}
        />
      </div>
      <div className="mb-8">
        <Multiselect
          addonBefore={<GlyphSearch />}
          className="mb-0"
          data={vacancyAutocompleteData || []}
          indent={false}
          loading={vacancyAutocompleteLoading}
          onChange={setVacancyData}
          onInputChange={setAutocompleteVacancy}
          placeholder="Название вакансии"
          shape="select"
          showArrow={false}
          value={vacancyData}
        />
      </div>
      <div className="mb-8">
        <Multiselect
          addonBefore={<GlyphSearch />}
          className="mb-0"
          data={recruiterAutocompleteData || []}
          indent={false}
          loading={recruiterAutocompleteLoading}
          onChange={setRecruiterData}
          onInputChange={setAutocompleteRecruiter}
          placeholder="Имя основного рекрутера"
          shape="select"
          showArrow={false}
          value={recruiterData}
        />
      </div>
      <div className="mb-8">
        <InputDatepicker
          classNameBackgroundColor={undefined}
          dates={state.datepickerValue}
          disabled={false}
          onBlurContainerField={undefined}
          onFocusContainerField={undefined}
          onSubmit={dates => handleChange("datepickerValue", dates)}
          placeholder={"Дата резюме"}
        />
      </div>
      <div className={cx(style.LabelSearchBlock, "mb-8")}>
        <Multiselect
          addonBefore={<GlyphSearch />}
          className="mb-0"
          data={labelAutocompleteData || []}
          indent={false}
          loading={labelAutocompleteLoading}
          onChange={setLabelData}
          onInputChange={setLabelAutocomplete}
          placeholder="Метки"
          shape="select"
          showArrow={false}
          value={labelData}
        />
        <RadioFormGroup
          defaultValue={"OR"}
          name={"labelSearchMode"}
          onChange={(e: any) => {
            setLabelSearchLogic(e.target.value as LabelSearchModel);
          }}
          shape={"horizontal"}
        >
          <Radio
            checked={labelSearchLogic === "OR"}
            label={"Или"}
            value={"OR"}
          />
          <Radio
            checked={labelSearchLogic === "AND"}
            label={"И"}
            value={"AND"}
          />
        </RadioFormGroup>
      </div>
      <div className="mb-8">
        <Checkbox
          checked={state.has_new_message}
          label="Есть новые сообщения"
          onChange={() =>
            handleChange("has_new_message", !state.has_new_message)
          }
        />
      </div>
      <Button onClick={handleReset} variant="secondary">
        Сбросить
      </Button>
      {ReactDOM.createPortal(
        <>
          <RightSidebar onClose={handleCloseSidebar} show={isRightSidebarOpen}>
            {isRightSidebarOpen && <Card candidate={candidateSelected} />}
          </RightSidebar>
        </>,
        document.body,
      )}
    </>
  );
};
export default FilterSideBar;
